import { useCallback, useEffect, useRef, useState } from "react";
import { useRobot } from "@/context/RobotContext";
import { X, WifiHigh, WifiSlash } from "@phosphor-icons/react";

const DEADZONE = 0.32;

function dirFromVector(nx, ny) {
  if (Math.hypot(nx, ny) < DEADZONE) return null;
  if (Math.abs(nx) > Math.abs(ny)) return nx > 0 ? ["RIGHT", "Right"] : ["LEFT", "Left"];
  return ny > 0 ? ["BACK", "Back"] : ["FWD", "Forward"];
}

export default function DriveMode({ onClose }) {
  const { sendCommand, status, deviceName } = useRobot();
  const baseRef = useRef(null);
  const activeRef = useRef(false);
  const lastDirRef = useRef(null);
  const [knob, setKnob] = useState({ x: 0, y: 0 });
  const [current, setCurrent] = useState("IDLE");

  const emit = useCallback(
    (payload, label) => {
      if (lastDirRef.current === payload) return;
      lastDirRef.current = payload;
      setCurrent(label);
      sendCommand(payload, label);
    },
    [sendCommand]
  );

  const stop = useCallback(() => {
    emit("STOP", "Stop");
    setCurrent("IDLE");
    lastDirRef.current = null;
  }, [emit]);

  const handleMove = useCallback(
    (clientX, clientY) => {
      const base = baseRef.current;
      if (!base) return;
      const rect = base.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const radius = rect.width / 2;
      let dx = clientX - cx;
      let dy = clientY - cy;
      const dist = Math.hypot(dx, dy);
      const clamped = Math.min(dist, radius);
      const angle = Math.atan2(dy, dx);
      const kx = Math.cos(angle) * clamped;
      const ky = Math.sin(angle) * clamped;
      setKnob({ x: kx, y: ky });
      const nx = kx / radius;
      const ny = ky / radius;
      const d = dirFromVector(nx, ny);
      if (d) emit(d[0], d[1]);
      else if (lastDirRef.current && lastDirRef.current !== "STOP") stop();
    },
    [emit, stop]
  );

  const onPointerDown = (e) => {
    activeRef.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    handleMove(e.clientX, e.clientY);
  };
  const onPointerMove = (e) => {
    if (!activeRef.current) return;
    handleMove(e.clientX, e.clientY);
  };
  const onPointerUp = () => {
    activeRef.current = false;
    setKnob({ x: 0, y: 0 });
    stop();
  };

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const connected = status === "connected";

  return (
    <div className="fixed inset-0 z-[60] bg-[#0A0B0E] grid-texture flex flex-col select-none" data-testid="drive-mode-overlay">
      {/* top bar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#272A35]">
        <span className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#FF5A00]">Drive Mode</span>
        <span
          className="flex items-center gap-2 text-xs uppercase font-bold"
          style={{ color: connected ? "#00E676" : "#FF3B30" }}
          data-testid="drive-status"
        >
          {connected ? <WifiHigh size={15} weight="bold" /> : <WifiSlash size={15} weight="bold" />}
          {connected ? deviceName || "linked" : "not connected"}
        </span>
        <button
          data-testid="drive-mode-close"
          onClick={onClose}
          className="flex items-center gap-1 text-xs uppercase tracking-widest text-[#94A3B8] hover:text-[#FF3B30] border border-[#272A35] hover:border-[#FF3B30] px-3 py-1.5 transition-colors"
        >
          <X size={14} weight="bold" /> Exit
        </button>
      </div>

      {/* current command readout */}
      <div className="text-center py-4">
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#475569]">Command</p>
        <p className="font-head font-black text-4xl sm:text-5xl tracking-tight" data-testid="drive-current-command" style={{ color: current === "IDLE" ? "#475569" : "#FF5A00" }}>
          {current}
        </p>
        {!connected && (
          <p className="text-[11px] text-[#FFB000] mt-1">Robot not connected — inputs are logged but not transmitted.</p>
        )}
      </div>

      {/* controls */}
      <div className="flex-1 flex items-center justify-between gap-6 px-6 sm:px-16 pb-10">
        {/* joystick */}
        <div
          ref={baseRef}
          data-testid="drive-joystick"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          className="relative rounded-full border-2 border-[#272A35] bg-[#111318] touch-none"
          style={{ width: "min(44vw, 300px)", height: "min(44vw, 300px)" }}
        >
          <div className="absolute left-1/2 top-3 -translate-x-1/2 text-[10px] text-[#475569]">FWD</div>
          <div className="absolute left-1/2 bottom-3 -translate-x-1/2 text-[10px] text-[#475569]">BACK</div>
          <div className="absolute top-1/2 left-3 -translate-y-1/2 text-[10px] text-[#475569]">L</div>
          <div className="absolute top-1/2 right-3 -translate-y-1/2 text-[10px] text-[#475569]">R</div>
          <div
            className="absolute left-1/2 top-1/2 rounded-full bg-[#FF5A00] border-4 border-[#0A0B0E] shadow-[0_0_24px_rgba(255,90,0,0.6)]"
            style={{
              width: "34%",
              height: "34%",
              transform: `translate(calc(-50% + ${knob.x}px), calc(-50% + ${knob.y}px))`,
              transition: activeRef.current ? "none" : "transform 0.18s ease-out",
            }}
          />
        </div>

        {/* stop */}
        <button
          data-testid="drive-stop-btn"
          onClick={stop}
          className="rounded-full border-2 border-[#FF3B30] text-[#FF3B30] font-head font-black uppercase tracking-widest hover:bg-[#FF3B30] hover:text-[#0A0B0E] transition-colors active:scale-95"
          style={{ width: "min(28vw, 160px)", height: "min(28vw, 160px)" }}
        >
          Stop
        </button>
      </div>
    </div>
  );
}
